import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ShoppingCart,
    X,
    Pencil,
    Trash2,
    Calendar,
    Clock,
    Tag,
    Loader2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Empty } from '../../../components/ui';
import UserService from '../../../services/user.service';
import { setCart } from '../../../redux/reducers/userSlice';
import { RootState } from '../../../redux/rootReducer';
import { formatCurrency } from '../../../components/custom-hooks';
import CustomDropdown from '../../../components/ui/CustomDropdown';
import { filterDaysOfTheWeek } from '../../../utils/Tools';
import { isAfricanCountry } from '../../../utils/constants';

interface Props {
    closeCart: () => void;
}

const TIMES_ = [
    '12:00AM', '1:00AM', '2:00AM', '3:00AM', '4:00AM', '5:00AM', '6:00AM', '7:00AM',
    '8:00AM', '9:00AM', '10:00AM', '11:00AM', '12:00PM', '1:00PM', '2:00PM', '3:00PM',
    '4:00PM', '5:00PM', '6:00PM', '7:00PM', '8:00PM', '9:00PM', '10:00PM', '11:00PM',
];

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatTime = (time: any) => TIMES_[time];
const formatDay = (day: any) => days[day];

const inputFieldStyle =
    'w-full rounded-xl border border-[#efe6f1] bg-[#FBF8FC] text-[14px] text-[#334155] px-4 py-2.5 outline-none focus:border-ryd-primary';

export default function CartModal({ closeCart }: Props) {
    const currencyInfo = useSelector((state: RootState) => state.user.currency);
    const userInfo: any = useSelector((state: RootState) => state.auth.userInfo);
    const userService = new UserService();
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(false);
    const [cartArr, setCartArr] = useState<any[]>([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [currency, setCurrency] = useState<any>(null);
    const [couponCode, setCouponCode] = useState('');

    const [editingId, setEditingId] = useState<number | null>(null);
    const [dayTime, setDayTime] = useState<any>([]);
    const [dayArr, setDayArr] = useState<{ name: string; value: number }[]>([]);
    const [timeArr, setTimeArr] = useState<{ name: string; value: number }[]>([]);
    const [selectedDay, setSelectedDay] = useState<{ name: string; value: number } | null>(null);
    const [selectedTime, setSelectedTime] = useState<{ name: string; value: number } | null>(null);
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
        if (currencyInfo && currencyInfo?.useRate) {
            setCurrency(currencyInfo);
        }
    }, [currencyInfo]);

    useEffect(() => {
        const african = isAfricanCountry(userInfo?.country);
        const rate = currency?.useRate ? Number(currency.rate) || 1 : 1;
        let total = 0;
        const items = Array.isArray(cartArr) ? cartArr : [];
        for (let i = 0; i < items.length; i++) {
            const pkg = items[i]?.programs?.[0]?.package;
            const base = african ? pkg?.altAmount : pkg?.amount;
            total += (base || 0) * rate;
        }
        setTotalAmount(total);
    }, [cartArr, currency, userInfo?.country]);

    useEffect(() => {
        getCart();
        getDayTime();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getDayTime = async () => {
        try {
            const response = await userService.getDayTime();
            if (!response.status) {
                toast.error(response.message);
                return;
            }
            setDayTime(response.data);
            if (response?.data?.length > 0) {
                setDayArr(filterDaysOfTheWeek(response?.data));
            }
        } catch (err: any) {
            toast.error(err?.message);
        }
    };

    const getCart = async () => {
        setLoading(true);
        try {
            const response = await userService.getCart();
            setLoading(false);
            if (!response.status) return;
            setCartArr(response.data);
            const programCount = Array.isArray(response.data) ? response.data.length : 0;
            dispatch(setCart(programCount > 0));
        } catch {
            setLoading(false);
        }
    };

    const handleItemDelete = async (id: number) => {
        setDeleteId(id);
        setDeleteLoading(true);
        try {
            const response = await userService.deleteProgram(id);
            setDeleteLoading(false);
            if (!response.status) {
                toast.error(response.message);
                return;
            }
            toast.success(response.message);
            await getCart();
        } catch (err: any) {
            setDeleteLoading(false);
            toast.error(err?.mesage || err?.message);
        }
    };

    const handleEditClick = (item: any) => {
        setEditingId(item.programs[0].id);
        const currentDay = item.programs[0].day;
        const dayMatch = dayArr.find((d) => d.value === currentDay);
        if (dayMatch) setSelectedDay(dayMatch);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setSelectedDay(null);
        setSelectedTime(null);
    };

    const handleUpdateDayTime = async (programId: number) => {
        if (!selectedDay || !selectedTime) {
            toast.error('Please select both day and time');
            return;
        }
        setUpdateLoading(true);
        try {
            const response = await userService.updateProgramDayTime(programId, {
                day: selectedDay.value,
                time: selectedTime.value,
            });
            if (!response.status) {
                toast.error(response.message);
                setUpdateLoading(false);
                return;
            }
            toast.success('Day and time updated successfully');
            setEditingId(null);
            setSelectedDay(null);
            setSelectedTime(null);
            setUpdateLoading(false);
            getCart();
        } catch (err: any) {
            setUpdateLoading(false);
            toast.error(err?.message || 'Failed to update');
        }
    };

    useEffect(() => {
        if (selectedDay) {
            const timeX = dayTime?.filter((item: any) => item.dayText === selectedDay.name);
            if (timeX && timeX.length > 0) {
                const tdx = timeX[0].times;
                const arr = [];
                for (let i = 0; i < tdx.length; i++) {
                    arr.push({ name: tdx[i].timeText, value: tdx[i].time });
                }
                setTimeArr(arr);
            }
        }
    }, [selectedDay, dayTime]);

    const handleCheckout = () => {
        if (couponCode.length > 0) {
            window.open(
                `https://api-pro.rydlearning.com/common/payment-init/${userInfo.id}/${couponCode}`,
                '_blank'
            );
        } else {
            window.open(`https://api-pro.rydlearning.com/common/payment-init/${userInfo.id}`, '_blank');
        }
        dispatch(setCart(false));
        closeCart();
    };

    const itemCount = Array.isArray(cartArr) ? cartArr.length : 0;

    return (
        <div className="relative">
            {/* Header */}
            <div className="flex items-start justify-between border-b border-[#f0eaf0] px-5 py-4 sm:px-6">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ryd-primaryLess1 text-ryd-primary">
                        <ShoppingCart size={20} />
                    </div>
                    <div>
                        <h1 className="font-[AvertaStd-Semibold] text-[18px] text-ryd-headerTextPrimary sm:text-[20px]">
                            Your Cart
                        </h1>
                        <p className="text-[12px] text-[#8b9aab]">
                            {itemCount === 0
                                ? 'No programs yet'
                                : `${itemCount} program${itemCount === 1 ? '' : 's'} ready for checkout`}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    aria-label="Close cart"
                    onClick={closeCart}
                    className="rounded-xl p-2 text-[#8b9aab] transition hover:bg-[#F8F5FA] hover:text-ryd-headerTextPrimary"
                >
                    <X size={18} />
                </button>
            </div>

            <div className="max-h-[min(65dvh,560px)] overflow-y-auto px-5 py-4 sm:px-6">
                {loading ? (
                    <div className="flex h-36 items-center justify-center">
                        <div className="h-14 w-14 animate-spin rounded-full border border-ryd-primary border-l-white" />
                    </div>
                ) : itemCount === 0 ? (
                    <div className="py-8">
                        <Empty text="No items in cart" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {(Array.isArray(cartArr) ? cartArr : []).map((item: any) => {
                            const prog = item?.programs?.[0];
                            const editing = editingId === prog?.id;
                            const rate = currency?.useRate ? Number(currency.rate) || 1 : 1;
                            const base = isAfricanCountry(userInfo.country)
                                ? prog?.package?.altAmount
                                : prog?.package?.amount;
                            const price = (base || 0) * rate;
                            const priceCode = currency?.currencyCode || (isAfricanCountry(userInfo.country) ? 'NGN' : 'CAD');

                            return (
                                <div
                                    key={item.id}
                                    className="rounded-[18px] border border-[#efe6f1] bg-[#FBF8FC]/80 p-4 transition hover:border-[#e8dce8]"
                                >
                                    {editing ? (
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-[15px] font-semibold capitalize text-ryd-headerTextPrimary">
                                                    {item?.firstName} {item?.lastName}
                                                </p>
                                                <p className="text-[13px] text-ryd-primary">{prog?.package?.title}</p>
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                                                    Lesson day
                                                </label>
                                                <CustomDropdown
                                                    className={inputFieldStyle}
                                                    handleChange={(data: { name: string; value: number }) =>
                                                        setSelectedDay(data)
                                                    }
                                                    data={dayArr}
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                                                    Lesson time
                                                </label>
                                                <CustomDropdown
                                                    className={inputFieldStyle}
                                                    handleChange={(data: { name: string; value: number }) =>
                                                        setSelectedTime(data)
                                                    }
                                                    data={timeArr}
                                                />
                                            </div>
                                            <div className="flex gap-2 pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleUpdateDayTime(prog.id)}
                                                    disabled={updateLoading}
                                                    className="flex-1 rounded-xl bg-ryd-primary py-2.5 text-[13px] font-semibold text-white disabled:opacity-60"
                                                >
                                                    {updateLoading ? 'Saving…' : 'Save'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleCancelEdit}
                                                    className="flex-1 rounded-xl border border-[#efe6f1] bg-white py-2.5 text-[13px] font-semibold text-[#475569]"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-ryd-primary to-[#6B1F66] text-[12px] font-bold text-white">
                                                        {`${item?.firstName?.charAt(0) || ''}${item?.lastName?.charAt(0) || ''}`.toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="truncate text-[15px] font-semibold capitalize text-ryd-headerTextPrimary">
                                                            {item?.firstName} {item?.lastName}
                                                        </p>
                                                        <p className="truncate text-[13px] font-medium text-ryd-primary">
                                                            {prog?.package?.title}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    <span className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-[#475569] border border-[#efe6f1]">
                                                        <Calendar size={12} className="text-ryd-primary" />
                                                        {formatDay(prog?.day) || 'N/A'}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-[#475569] border border-[#efe6f1]">
                                                        <Clock size={12} className="text-ryd-primary" />
                                                        {formatTime(prog?.time) || 'N/A'}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-[#475569] border border-[#efe6f1]">
                                                        8 weeks
                                                    </span>
                                                </div>

                                                <div className="mt-2 space-y-0.5 text-[12px] text-[#8b9aab]">
                                                    <p>
                                                        Next class:{' '}
                                                        {prog?.nextClassDate
                                                            ? new Date(prog.nextClassDate).toLocaleDateString()
                                                            : 'N/A'}
                                                    </p>
                                                    <p>
                                                        End date:{' '}
                                                        {prog?.endClassDate
                                                            ? new Date(prog.endClassDate).toLocaleDateString()
                                                            : 'N/A'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex shrink-0 items-center gap-2 sm:flex-col sm:items-end">
                                                {price != null && (
                                                    <p className="text-[15px] font-semibold text-ryd-headerTextPrimary">
                                                        {priceCode} {formatCurrency(price)}
                                                    </p>
                                                )}
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        title="Edit schedule"
                                                        onClick={() => handleEditClick(item)}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#efe6f1] bg-white text-ryd-primary transition hover:border-ryd-primary"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Remove item"
                                                        onClick={() => handleItemDelete(prog?.id)}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600 transition hover:bg-red-100"
                                                    >
                                                        {deleteLoading && deleteId === prog?.id ? (
                                                            <Loader2 size={14} className="animate-spin" />
                                                        ) : (
                                                            <Trash2 size={14} />
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {itemCount > 0 && !loading && (
                <div className="border-t border-[#f0eaf0] bg-white px-5 py-4 sm:px-6">
                    <div className="mb-3 flex items-center gap-2 rounded-xl border border-[#efe6f1] bg-[#FBF8FC] px-3 py-2.5">
                        <Tag size={15} className="shrink-0 text-ryd-primary" />
                        <input
                            placeholder="Enter promo code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            className="min-w-0 flex-1 bg-transparent text-[13px] text-[#334155] outline-none placeholder:text-[#94a3b8]"
                        />
                    </div>

                    <div className="mb-4 flex items-center justify-between">
                        <p className="text-[13px] text-[#8b9aab]">Total</p>
                        <p className="font-[AvertaStd-Semibold] text-[20px] text-ryd-headerTextPrimary">
                            {currency?.currencyCode || (isAfricanCountry(userInfo?.country) ? 'NGN' : 'CAD')} {formatCurrency(totalAmount)}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleCheckout}
                        className="w-full rounded-[14px] bg-gradient-to-r from-[#7A2F78] via-ryd-primary to-[#E05AAE] py-3.5 text-[14px] font-semibold text-white shadow-md transition hover:scale-[1.01]"
                    >
                        Proceed to checkout
                    </button>
                </div>
            )}
        </div>
    );
}
